import { useState, useEffect } from "react";

import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import api from "../../api/client";

const COMPANY_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+",
];

function getPasswordStrength(password) {
  if (!password) return null;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  if (passed <= 1)
    return {
      label: "Weak",
      color: "bg-red-400",
      width: "w-1/4",
      textColor: "text-red-500",
    };
  if (passed === 2)
    return {
      label: "Fair",
      color: "bg-amber-400",
      width: "w-2/4",
      textColor: "text-amber-500",
    };
  if (passed === 3)
    return {
      label: "Good",
      color: "bg-blue-400",
      width: "w-3/4",
      textColor: "text-blue-500",
    };
  return {
    label: "Strong",
    color: "bg-green-400",
    width: "w-full",
    textColor: "text-green-500",
  };
}

function validatePassword(password) {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(password))
    return "Password must contain at least one uppercase letter";
  if (!/\d/.test(password)) return "Password must contain at least one number";
  return null;
}

export default function EmployerRegister() {
  const navigate = useNavigate();
  const setEmployerAuth = useAuthStore((s) => s.setEmployerAuth);

  const [form, setForm] = useState({
    company_name: "",
    company_website: "",
    company_size: "",
    email: "",
    password: "",
    is_agency: false,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Employer Portal";
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pwError = validatePassword(form.password);
    if (pwError) {
      setError(pwError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/employer/auth/register", form, {
        withCredentials: true,
      });
      setEmployerAuth(data.access_token, data.employer);
      navigate(
        "/employers/verify-pending?email=" + encodeURIComponent(form.email),
      );
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(form.password);
  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <Logo className="h-12" />
        <ThemeToggle />
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8 w-full max-w-md">
          <div className="mb-6">
            <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest">
              Employer portal
            </span>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              Create your employer account
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Start integrating with your candidates on Joborigo.
            </p>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className={labelClass}>Company name *</label>
              <input
                name="company_name"
                required
                value={form.company_name}
                onChange={handleChange}
                placeholder="Acme Corp"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Company website</label>
              <input
                name="company_website"
                type="url"
                value={form.company_website}
                onChange={handleChange}
                placeholder="https://acme.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Company size</label>
              <select
                name="company_size"
                value={form.company_size}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Select size...</option>
                {COMPANY_SIZES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-xl border border-gray-100 dark:border-white/10">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Recruitment agency
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  We are a staffing firm or headhunter, not a direct employer
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={form.is_agency}
                    onChange={(e) =>
                      setForm({ ...form, is_agency: e.target.checked })
                    }
                  />
                  <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-violet transition" />
                  <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-4" />
                </div>
              </label>
            </div>
            <div>
              <label className={labelClass}>Work email *</label>
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Password *</label>
              <input
                name="password"
                type="password"
                required
                value={form.password}
                onChange={handleChange}
                placeholder="Min 8 characters"
                className={inputClass}
              />
              {strength && (
                <div className="mt-2">
                  <div className="h-1 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className={`text-xs font-medium ${strength.textColor}`}>
                      {strength.label}
                    </p>
                    <p className="text-xs text-gray-400">
                      {!/[A-Z]/.test(form.password) && "Add uppercase "}
                      {!/\d/.test(form.password) && "Add number "}
                      {!/[^A-Za-z0-9]/.test(form.password) && "Add symbol"}
                    </p>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1.5">
                Min 8 characters, one uppercase letter and one number
              </p>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed">
              By registering you agree to our{" "}
              <Link to="/terms" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
                Privacy Policy
              </Link>
              .
            </p>

            <button
              type="submit"
              disabled={loading}
              className="bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Creating account..." : "Create employer account"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            Already have an account?{" "}
            <Link
              to="/employers/login"
              className="text-brand-violet dark:text-brand-violet-on-dark font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
          <p className="text-center text-sm text-gray-400 mt-2">
            Looking for the job seeker app?{" "}
            <Link to="/register" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
              Register as a seeker
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
